import { orderBy } from 'lodash';
import { ReactElement, useMemo } from 'react';
import { PlanEntitlementKey } from '../../../../../api/graphql/generated';
import { useTeamRoleContext } from '../../../../../api/hooks/role.hooks';
import { usePaywallActionModalLazy } from '../../../../../components/paywall/paywall-modal-wrapper';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import {
  FlowItemParameterMetadata,
  FlowItemParameterRef,
  SystemUnit,
  WidgetType,
} from '../../model/flow.types';
import { convertParameterMetadata } from '../../util/flow-parameter.util';
import { PARAMETER_LIST_FORM_PREFIX } from '../flow-item/edit/flow-item-form';
import { ColorPickerInput } from '../widget/color-picker';
import { NumericInputWidget } from '../widget/numeric-input';
import { PickerInput } from '../widget/picker-input';
import { SliderInput } from '../widget/slider-input';
import { SwitchInput } from '../widget/switch-input';
import { TextInput } from '../widget/text-input';
import { WidgetLoading } from '../widget/widget-loading';
import { WidgetExtensions, WidgetProps } from '../widget/widget.types';
import {
  useParameterDisplay,
  useParameterExtensions,
  useParameterPaywallLogic,
  useParameterState,
  useParameterVisibility,
} from './parameter-list.hooks';
import './parameter-list.css';

export type ParametersRecommendationInfo<
  ApplicationFlowItemRef extends string = FlowItemParameterRef,
> = Partial<
  Record<
    ApplicationFlowItemRef,
    {
      subtext?: string;
      subtitleColor?: string;
      loading?: boolean;
    }
  >
>;

export interface ParameterListExtensions {
  widgetExtensions?: WidgetExtensions;
  allowedParametersMap?: Record<string, boolean>;
}

interface ParametersListProps {
  system: SystemUnit;
  formPrefix?: string;
  parameters: FlowItemParameterMetadata[];
  parametersRecommendationInfo?: ParametersRecommendationInfo;
  parameterPermissions?: Record<string, boolean>;
  extensions?: ParameterListExtensions;
  readOnly?: boolean;
}

interface ParameterProps {
  parameter: FlowItemParameterMetadata;
  index: number;
  totalParameters: number;
  system: SystemUnit;
  formPrefix: string;
  parametersRecommendationInfo?: ParametersRecommendationInfo;
  parameterPermissions: Record<string, boolean>;
  extensions: ParameterListExtensions;
  readOnly?: boolean;
  hasRoleFailure: boolean;
  onPaywallAction?: (params: {
    planEntitlementKey: PlanEntitlementKey;
    machineId?: string;
    action: () => void;
  }) => void;
}

interface CreateWidgetComponentProps {
  widget: WidgetType | undefined;
  parameter: FlowItemParameterMetadata;
  name: string;
  key: string;
  effectiveDescription: string | undefined;
  isDisabledOrReadonly: boolean;
  sharedProps: WidgetProps;
}

// Utility function to create widget component
const createWidgetComponent = (
  props: CreateWidgetComponentProps
): ReactElement | null => {
  const {
    widget,
    parameter,
    name,
    key,
    effectiveDescription,
    isDisabledOrReadonly,
    sharedProps,
  } = props;
  const { value, ui } = parameter;
  const { minimum, maximum } = value ?? {};
  const { widgetOptions } = ui ?? {};
  if (widget === WidgetType.SLIDER) {
    return (
      <SliderInput
        key={key}
        name={name}
        description={effectiveDescription}
        disabled={isDisabledOrReadonly}
        min={minimum ?? 0}
        max={maximum ?? 100}
        step={widgetOptions.step ?? 1}
        {...sharedProps}
      />
    );
  }

  if (widget === WidgetType.PICKER) {
    return (
      <PickerInput
        key={key}
        name={name}
        disabled={isDisabledOrReadonly}
        options={widgetOptions}
        placeholder={getLocalizedText('common:make_selection')}
        {...sharedProps}
      />
    );
  }

  if (widget === WidgetType.NUMBER) {
    return (
      <NumericInputWidget
        key={key}
        name={name}
        description={effectiveDescription}
        disabled={isDisabledOrReadonly}
        step={widgetOptions?.step}
        {...sharedProps}
      />
    );
  }

  if (widget === WidgetType.TEXT) {
    return (
      <TextInput
        key={key}
        name={name}
        description={effectiveDescription}
        disabled={isDisabledOrReadonly}
        {...sharedProps}
      />
    );
  }

  if (widget === WidgetType.SWITCH) {
    return (
      <SwitchInput
        key={key}
        name={name}
        description={effectiveDescription}
        disabled={isDisabledOrReadonly}
        {...sharedProps}
      />
    );
  }

  if (widget === WidgetType.COLOR_PICKER) {
    return (
      <ColorPickerInput
        key={key}
        name={name}
        description={effectiveDescription}
        {...sharedProps}
      />
    );
  }

  return null;
};

const Parameter = (props: ParameterProps): ReactElement | null => {
  const {
    parameter,
    index,
    totalParameters,
    system,
    formPrefix,
    parametersRecommendationInfo,
    parameterPermissions,
    extensions,
    readOnly,
    hasRoleFailure,
    onPaywallAction,
  } = props;

  // Custom hooks for business logic
  const { shouldRender } = useParameterVisibility(parameter);
  const {
    isDisabledOrReadonly,
    isReadonlyFromCondition,
    effectiveDescription,
    showDescription,
  } = useParameterState({
    parameter,
    formPrefix,
    readOnly,
    hasRoleFailure,
    parameterPermissions,
  });
  const { parameterRefExtensions } = useParameterExtensions({
    parameter,
    extensions,
    isReadonlyFromCondition,
  });
  const { label, subtitle, subtitleColor, loading } = useParameterDisplay({
    parameter,
    system,
    parametersRecommendationInfo,
  });
  const { isAvailable, isPaywalled, handlePaywallClick } =
    useParameterPaywallLogic({ parameter, onPaywallAction });

  // Early return if parameter should not be rendered
  if (!shouldRender) {
    return null;
  }

  // Computed values
  const isLastItem = totalParameters === index + 1;
  const name = `${formPrefix}.${parameter.ref}.value`;
  const key = `${name}-${index}`;
  const widget = parameter.ui?.widget;
  const widgetOptions = parameter.ui?.widgetOptions;

  const sharedProps: WidgetProps = {
    label,
    description: effectiveDescription,
    showDescription,
    ionItemProps: { lines: isLastItem ? 'none' : 'full' },
    extensions: parameterRefExtensions,
    subtitle,
    subtitleColor,
    isPaywalled,
  };

  // Create widget component
  let widgetComponent: ReactElement | null = null;

  if (loading === true) {
    widgetComponent = (
      <WidgetLoading
        key={key}
        name={name}
        description={effectiveDescription}
        {...sharedProps}
      />
    );
  } else {
    widgetComponent = createWidgetComponent({
      widget,
      parameter,
      name,
      key,
      effectiveDescription,
      isDisabledOrReadonly,
      sharedProps,
    });
  }

  // If no widget component, return null
  if (!widgetComponent) {
    return null;
  }

  // Return appropriate component based on paywall status
  if (isAvailable) {
    return widgetComponent;
  }

  return (
    <div
      className={`parameter-paywall-wrapper ${
        isPaywalled ? 'parameter-paywall-wrapper--paywalled' : ''
      }`}
      onClick={handlePaywallClick}
    >
      {widgetComponent}
    </div>
  );
};

export const ParametersList = (props: ParametersListProps): ReactElement => {
  const {
    parameters,
    parametersRecommendationInfo,
    system,
    readOnly,
    parameterPermissions = {},
    extensions = {},
  } = props;

  const { hasError: hasRoleFailure } = useTeamRoleContext();
  // We keep the paywall modal in one place so that it can be shared between parameters
  const { paywallAction, paywallModal } = usePaywallActionModalLazy();

  const convertedParameters = useMemo(() => {
    const converted = parameters.map((p) =>
      convertParameterMetadata(p, system)
    );
    return orderBy(
      converted.filter((p) => p.displayOrder !== undefined),
      ['displayOrder'],
      ['asc']
    );
  }, [parameters, system]);

  const formPrefix = props.formPrefix ?? PARAMETER_LIST_FORM_PREFIX;

  return (
    <>
      {convertedParameters.map(
        (p: FlowItemParameterMetadata, index: number) => (
          <Parameter
            key={`${formPrefix}.${p.ref}.value-${index}`}
            parameter={p}
            index={index}
            totalParameters={convertedParameters.length}
            system={system}
            formPrefix={formPrefix}
            parametersRecommendationInfo={parametersRecommendationInfo}
            parameterPermissions={parameterPermissions}
            extensions={extensions}
            readOnly={readOnly}
            hasRoleFailure={hasRoleFailure}
            onPaywallAction={paywallAction}
          />
        )
      )}
      <div style={{ padding: 8 }} />
      {paywallModal}
    </>
  );
};
